/** Перенос `domain/models/app_state/app_state.dart`. */

import type { AppPackageInfo } from '@/domain/models/appPackageInfo';
import type { AppUpdateRequiredInfo } from '@/domain/models/appUpdateRequiredInfo';
import type { BaseUrlState } from '@/domain/models/baseUrlState';
import type { ConfigModel } from '@/domain/models/configModel';
import type { PersonModel } from '@/domain/models/personModel';
import { versionGreaterThan } from '@/domain/models/versionModel';
import { type Nullable, resolveNullable } from '@/utils/nullable';

export interface AppState {
  readonly config: ConfigModel | null;
  readonly person: PersonModel | null;
  readonly baseUrl: BaseUrlState | null;
  readonly packageInfo: AppPackageInfo;
}

/**
 * Патч для [appStateCopyWith]. Обнуляемые поля идут в `Nullable`:
 * без обёртки поле не трогается, `new Nullable(null)` стирает его — так же
 * работал sentinel `freezed` в Dart. Обязательный `packageInfo` обнулить
 * нельзя, поэтому обёртка ему не нужна.
 */
export interface AppStatePatch {
  readonly config?: Nullable<ConfigModel | null>;
  readonly person?: Nullable<PersonModel | null>;
  readonly baseUrl?: Nullable<BaseUrlState | null>;
  readonly packageInfo?: AppPackageInfo;
}

/** `AppState.copyWith` — всегда новый объект, исходный не меняется. */
export function appStateCopyWith(state: AppState, patch: AppStatePatch): AppState {
  return {
    config: resolveNullable(patch.config, state.config),
    person: resolveNullable(patch.person, state.person),
    baseUrl: resolveNullable(patch.baseUrl, state.baseUrl),
    packageInfo: patch.packageInfo ?? state.packageInfo,
  };
}

/**
 * Перенос геттера `AppState.updateRequired`.
 *
 * Скачивание и установка APK из порта убраны вместе с остальной
 * Android-частью, но сам расчёт остался чистой функцией: хранилище
 * состояния по-прежнему его отдаёт.
 */
export function appStateUpdateRequired(state: AppState): AppUpdateRequiredInfo | null {
  const updateConfig = state.config?.updateConfig ?? null;
  if (updateConfig === null) {
    return null;
  }
  const currentFullVersion = state.packageInfo.version;
  const serverFullVersion = updateConfig.version;
  const serverVersion = serverFullVersion.appVersion;
  const currentVersion = currentFullVersion.appVersion;
  if (versionGreaterThan(serverVersion, currentVersion)) {
    return {
      updateLink: updateConfig.appUrl,
      currentVersion: currentVersion,
      updateVersion: serverVersion,
    };
  } else {
    return null;
  }
}
